import React, { useCallback, useEffect, useMemo, useState } from 'react';
import supabase from '../config/supabase';
import {
  createVendorAliasIfNeeded,
  normalizeVendorName,
  renameVendorAndCreateAlias,
  resolveVendorByNameOrAlias
} from '../utils/vendorResolution';

const DAYS = [
  { name: 'Monday', short: 'Mon' },
  { name: 'Tuesday', short: 'Tue' },
  { name: 'Wednesday', short: 'Wed' },
  { name: 'Thursday', short: 'Thu' },
  { name: 'Friday', short: 'Fri' },
  { name: 'Saturday', short: 'Sat' },
  { name: 'Sunday', short: 'Sun' }
];

const toBitmask = (dayNames) =>
  dayNames.reduce((mask, dayName) => {
    const dayIndex = DAYS.findIndex((day) => day.name === dayName);
    return dayIndex === -1 ? mask : mask | (1 << dayIndex);
  }, 0);

const fromBitmask = (mask) =>
  DAYS.filter((_, index) => mask & (1 << index)).map((day) => day.name);

const dbTimeToInputTime = (value) => {
  if (!value) return '';
  return value.slice(0, 5);
};

const inputTimeToDbTime = (value) => {
  if (!value) return null;
  return `${value}:00`;
};

const getAliasText = (aliasRow) => normalizeVendorName(aliasRow?.alias_name || aliasRow?.alias || '');

const getEmptyVendorForm = () => ({
  display_name: '',
  legal_name: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  contact2_name: '',
  contact2_email: '',
  contact2_phone: '',
  general_phone: '',
  general_email: '',
  address: '',
  website: '',
  payment_terms: '',
  minimum_order: '',
  ordering_method: 'email',
  lead_days: '',
  account_number: '',
  delivery_days: '',
  notes: '',
  status: 'active',
  cutoff_time: ''
});

const GroupVendorOrderEditor = ({ enabled, userStore, vendorNames = [], notify = () => {} }) => {
  const [selectedVendorName, setSelectedVendorName] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [newAlias, setNewAlias] = useState('');
  const [selectedDays, setSelectedDays] = useState([]);
  const [storeVendorNames, setStoreVendorNames] = useState([]);
  const [vendorForm, setVendorForm] = useState(getEmptyVendorForm());
  const [loadingVendor, setLoadingVendor] = useState(false);
  const [savingVendor, setSavingVendor] = useState(false);

  const uniqueVendorNames = useMemo(
    () =>
      Array.from(
        new Set(
          [...vendorNames, ...storeVendorNames]
            .map((name) => (name || '').trim())
            .filter(Boolean)
        )
      ).sort(),
    [vendorNames, storeVendorNames]
  );

  useEffect(() => {
    const loadStoreVendors = async () => {
      if (!enabled || !userStore?.store_id) {
        setStoreVendorNames([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('vendors')
          .select('display_name')
          .eq('store_id', userStore.store_id)
          .order('display_name', { ascending: true });
        if (error) throw error;
        setStoreVendorNames((data || []).map((row) => row.display_name).filter(Boolean));
      } catch (error) {
        console.warn('Could not load vendor list for group order editor:', error?.message);
        setStoreVendorNames([]);
      }
    };

    loadStoreVendors();
  }, [enabled, userStore]);

  useEffect(() => {
    if (!uniqueVendorNames.length) {
      setSelectedVendorName('');
      setSelectedVendor(null);
      setAliases([]);
      setSelectedDays([]);
      setVendorForm(getEmptyVendorForm());
      return;
    }
    if (!selectedVendorName || !uniqueVendorNames.includes(selectedVendorName)) {
      setSelectedVendorName(uniqueVendorNames[0]);
    }
  }, [uniqueVendorNames, selectedVendorName]);

  const setFormFromVendor = useCallback((vendorRow) => {
    setVendorForm({
      display_name: vendorRow.display_name || '',
      legal_name: vendorRow.legal_name || '',
      contact_name: vendorRow.contact_name || '',
      contact_email: vendorRow.contact_email || '',
      contact_phone: vendorRow.contact_phone || '',
      contact2_name: vendorRow.contact2_name || '',
      contact2_email: vendorRow.contact2_email || '',
      contact2_phone: vendorRow.contact2_phone || '',
      general_phone: vendorRow.general_phone || '',
      general_email: vendorRow.general_email || '',
      address: vendorRow.address || '',
      website: vendorRow.website || '',
      payment_terms: vendorRow.payment_terms || '',
      minimum_order: vendorRow.minimum_order || '',
      ordering_method: vendorRow.ordering_method || 'email',
      lead_days: vendorRow.lead_days ?? '',
      account_number: vendorRow.account_number || '',
      delivery_days: vendorRow.delivery_days || '',
      notes: vendorRow.notes || '',
      status: vendorRow.status || 'active',
      cutoff_time: dbTimeToInputTime(vendorRow.cutoff_time)
    });
    setSelectedDays(fromBitmask(vendorRow.ordering_days || 0));
    setAliases(vendorRow.vendor_aliases || []);
  }, []);

  const resolveVendorId = useCallback(
    async (vendorName) => {
      const cleanName = normalizeVendorName(vendorName);
      if (!cleanName || !userStore?.store_id) return null;
      const resolved = await resolveVendorByNameOrAlias({
        supabaseClient: supabase,
        storeId: userStore.store_id,
        vendorName: cleanName,
        stubNote: 'Auto-discovered from inventory data',
        createIfMissing: true
      });
      return resolved?.vendorId || null;
    },
    [userStore]
  );

  const loadVendor = useCallback(async () => {
    if (!enabled || !selectedVendorName || !userStore?.store_id) return;
    setLoadingVendor(true);
    try {
      const vendorId = await resolveVendorId(selectedVendorName);
      if (!vendorId) return;

      const { data: vendorRow, error } = await supabase
        .from('vendors')
        .select('*, vendor_aliases(id, alias, alias_name, created_at)')
        .eq('id', vendorId)
        .single();

      if (error) throw error;

      setSelectedVendor(vendorRow);
      setFormFromVendor(vendorRow);
    } catch (error) {
      console.error('Error loading vendor for group order editor:', error);
      notify('Could not load vendor details for this group item.', 'error');
    } finally {
      setLoadingVendor(false);
    }
  }, [enabled, selectedVendorName, userStore, resolveVendorId, setFormFromVendor, notify]);

  useEffect(() => {
    loadVendor();
  }, [loadVendor]);

  const handleVendorFieldChange = (field, value) => {
    setVendorForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggleDay = (dayName) => {
    setSelectedDays((prev) =>
      prev.includes(dayName) ? prev.filter((day) => day !== dayName) : [...prev, dayName]
    );
  };

  const applyDayPreset = (presetKey) => {
    if (presetKey === 'weekdays') {
      setSelectedDays(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']);
      return;
    }
    if (presetKey === 'mwf') {
      setSelectedDays(['Monday', 'Wednesday', 'Friday']);
      return;
    }
    if (presetKey === 'tth') {
      setSelectedDays(['Tuesday', 'Thursday']);
      return;
    }
    if (presetKey === 'weekend') {
      setSelectedDays(['Saturday', 'Sunday']);
      return;
    }
    setSelectedDays([]);
  };

  const handleAddAlias = async () => {
    if (!selectedVendor?.id || !newAlias.trim() || !userStore?.store_id) return;
    const trimmedAlias = normalizeVendorName(newAlias);

    const alreadyExists = aliases.some(
      (aliasRow) => getAliasText(aliasRow).toLowerCase() === trimmedAlias.toLowerCase()
    );
    if (alreadyExists) {
      notify('Alias already exists.', 'error');
      return;
    }

    try {
      await createVendorAliasIfNeeded({
        supabaseClient: supabase,
        storeId: userStore.store_id,
        vendorId: selectedVendor.id,
        aliasName: trimmedAlias
      });
      await loadVendor();
      setNewAlias('');
      notify('Alias added.', 'success');
    } catch (error) {
      console.error('Error adding vendor alias from group editor:', error);
      notify('Could not add alias.', 'error');
    }
  };

  const handleRemoveAlias = async (aliasId) => {
    if (!aliasId) return;
    try {
      const { error } = await supabase.from('vendor_aliases').delete().eq('id', aliasId);
      if (error) throw error;
      setAliases((prev) => prev.filter((aliasRow) => aliasRow.id !== aliasId));
    } catch (error) {
      console.error('Error removing vendor alias from group editor:', error);
      notify('Could not remove alias.', 'error');
    }
  };

  const handleSaveVendor = async () => {
    if (!selectedVendor?.id) {
      notify('Select a vendor to edit.', 'error');
      return;
    }
    if (!vendorForm.display_name.trim()) {
      notify('Display name is required.', 'error');
      return;
    }

    setSavingVendor(true);
    try {
      const hasPrimaryContact = Boolean(
        vendorForm.contact_email?.trim() &&
          (vendorForm.contact_name?.trim() || vendorForm.contact_phone?.trim())
      );

      const nextDisplayName = normalizeVendorName(vendorForm.display_name);
      const payload = {
        display_name: nextDisplayName,
        legal_name: vendorForm.legal_name.trim() || null,
        contact_name: vendorForm.contact_name.trim() || null,
        contact_email: vendorForm.contact_email.trim() || null,
        contact_phone: vendorForm.contact_phone.trim() || null,
        contact2_name: vendorForm.contact2_name.trim() || null,
        contact2_email: vendorForm.contact2_email.trim() || null,
        contact2_phone: vendorForm.contact2_phone.trim() || null,
        general_phone: vendorForm.general_phone.trim() || null,
        general_email: vendorForm.general_email.trim() || null,
        address: vendorForm.address.trim() || null,
        website: vendorForm.website.trim() || null,
        payment_terms: vendorForm.payment_terms.trim() || null,
        minimum_order: vendorForm.minimum_order.trim() || null,
        ordering_method: vendorForm.ordering_method || 'email',
        lead_days: vendorForm.lead_days === '' ? 0 : parseInt(vendorForm.lead_days, 10) || 0,
        account_number: vendorForm.account_number.trim() || null,
        delivery_days: vendorForm.delivery_days.trim() || null,
        notes: vendorForm.notes.trim() || null,
        status: vendorForm.status || 'active',
        ordering_days: toBitmask(selectedDays),
        cutoff_time: inputTimeToDbTime(vendorForm.cutoff_time)
      };

      if (Object.prototype.hasOwnProperty.call(selectedVendor || {}, 'vendor_status')) {
        payload.vendor_status = hasPrimaryContact ? 'active' : 'needs_details';
      }

      // First persist non-name fields.
      const { error: updateError } = await supabase
        .from('vendors')
        .update({ ...payload, display_name: selectedVendor.display_name })
        .eq('id', selectedVendor.id);
      if (updateError) throw updateError;

      // Then handle rename through shared flow (ensures old name alias is created).
      if (normalizeVendorName(selectedVendor.display_name).toLowerCase() !== nextDisplayName.toLowerCase()) {
        await renameVendorAndCreateAlias({
          supabaseClient: supabase,
          storeId: userStore.store_id,
          vendorId: selectedVendor.id,
          newDisplayName: nextDisplayName
        });
      } else {
        // Still make sure selected dropdown text resolves in future.
        await createVendorAliasIfNeeded({
          supabaseClient: supabase,
          storeId: userStore.store_id,
          vendorId: selectedVendor.id,
          aliasName: selectedVendorName
        });
      }

      notify('Vendor ordering/contact details saved.', 'success');
      await loadVendor();
      // Broadcast so other components (item lists, count views, etc.) can
      // reflect the updated vendor name without requiring a page reload.
      document.dispatchEvent(new CustomEvent('vendorDataChanged', {
        detail: { storeId: userStore?.store_id }
      }));
    } catch (error) {
      console.error('Error saving vendor from group editor:', error);
      notify('Could not save vendor details.', 'error');
    } finally {
      setSavingVendor(false);
    }
  };

  const vendorCompletion = useMemo(() => {
    const hasEmail = Boolean(vendorForm.contact_email?.trim());
    const hasPrimary = Boolean(vendorForm.contact_name?.trim() || vendorForm.contact_phone?.trim());
    return hasEmail && hasPrimary ? 'Complete' : 'Needs details';
  }, [vendorForm.contact_email, vendorForm.contact_name, vendorForm.contact_phone]);

  if (!enabled) return null;

  return (
    <div className="card mt-3">
      <div className="card-header py-2 d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Vendor order details (order mode)</h6>
        <span className={`badge ${vendorCompletion === 'Complete' ? 'bg-success' : 'bg-warning text-dark'}`}>
          {vendorCompletion}
        </span>
      </div>
      <div className="card-body">
        {!uniqueVendorNames.length ? (
          <p className="small text-muted mb-0">
            Add group items with vendors to edit delivery schedules and contacts.
          </p>
        ) : (
          <>
            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <label className="form-label small mb-1">Vendor in this group</label>
                <select
                  className="form-select form-select-sm"
                  value={selectedVendorName}
                  onChange={(e) => setSelectedVendorName(e.target.value)}
                >
                  {uniqueVendorNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 d-flex align-items-end justify-content-md-end">
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={handleSaveVendor}
                  disabled={savingVendor || loadingVendor || !selectedVendor}
                >
                  {savingVendor ? 'Saving…' : '✓ Save vendor details'}
                </button>
              </div>
            </div>

            {loadingVendor ? (
              <p className="small text-muted mb-0">Loading vendor details…</p>
            ) : (
              <div className="d-flex flex-column gap-3">
                <div>
                  <h6 className="small text-uppercase text-muted mb-2">Names</h6>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small mb-1">Display Name</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={vendorForm.display_name}
                        onChange={(e) => handleVendorFieldChange('display_name', e.target.value)}
                        placeholder="Another Brand"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small mb-1">Legal Name</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={vendorForm.legal_name}
                        onChange={(e) => handleVendorFieldChange('legal_name', e.target.value)}
                        placeholder="Legal / Full Name"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small mb-1">Aliases</label>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {aliases.length ? (
                          aliases.map((aliasRow) => (
                            <span key={aliasRow.id} className="badge bg-light text-dark border">
                              {getAliasText(aliasRow)}
                              <button
                                type="button"
                                className="btn btn-sm p-0 ms-1 border-0 bg-transparent text-danger"
                                onClick={() => handleRemoveAlias(aliasRow.id)}
                                aria-label={`Remove alias ${getAliasText(aliasRow)}`}
                              >
                                ×
                              </button>
                            </span>
                          ))
                        ) : (
                          <span className="small text-muted fst-italic">No aliases yet</span>
                        )}
                      </div>
                      <div className="input-group input-group-sm">
                        <input
                          type="text"
                          className="form-control"
                          value={newAlias}
                          onChange={(e) => setNewAlias(e.target.value)}
                          placeholder="Add alias (e.g. Freds Beer LA)"
                        />
                        <button type="button" className="btn btn-outline-secondary" onClick={handleAddAlias}>
                          + Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h6 className="small text-uppercase text-muted mb-2">Primary Contact</h6>
                  <div className="row g-2">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={vendorForm.contact_name}
                        onChange={(e) => handleVendorFieldChange('contact_name', e.target.value)}
                        placeholder="Contact Name"
                      />
                    </div>
                    <div className="col-md-4">
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        value={vendorForm.contact_email}
                        onChange={(e) => handleVendorFieldChange('contact_email', e.target.value)}
                        placeholder="Email"
                      />
                    </div>
                    <div className="col-md-4">
                      <input
                        type="tel"
                        className="form-control form-control-sm"
                        value={vendorForm.contact_phone}
                        onChange={(e) => handleVendorFieldChange('contact_phone', e.target.value)}
                        placeholder="Phone"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h6 className="small text-uppercase text-muted mb-2">Secondary Contact</h6>
                  <div className="row g-2">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={vendorForm.contact2_name}
                        onChange={(e) => handleVendorFieldChange('contact2_name', e.target.value)}
                        placeholder="Contact Name"
                      />
                    </div>
                    <div className="col-md-4">
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        value={vendorForm.contact2_email}
                        onChange={(e) => handleVendorFieldChange('contact2_email', e.target.value)}
                        placeholder="Email"
                      />
                    </div>
                    <div className="col-md-4">
                      <input
                        type="tel"
                        className="form-control form-control-sm"
                        value={vendorForm.contact2_phone}
                        onChange={(e) => handleVendorFieldChange('contact2_phone', e.target.value)}
                        placeholder="Phone"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h6 className="small text-uppercase text-muted mb-2">General</h6>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <input
                        type="tel"
                        className="form-control form-control-sm"
                        value={vendorForm.general_phone}
                        onChange={(e) => handleVendorFieldChange('general_phone', e.target.value)}
                        placeholder="General Phone"
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        value={vendorForm.general_email}
                        onChange={(e) => handleVendorFieldChange('general_email', e.target.value)}
                        placeholder="General Email"
                      />
                    </div>
                  </div>
                </div>

                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label small mb-1">Address</label>
                    <textarea
                      rows={2}
                      className="form-control form-control-sm"
                      value={vendorForm.address}
                      onChange={(e) => handleVendorFieldChange('address', e.target.value)}
                      placeholder="Address"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small mb-1">Website</label>
                    <input
                      type="url"
                      className="form-control form-control-sm mb-2"
                      value={vendorForm.website}
                      onChange={(e) => handleVendorFieldChange('website', e.target.value)}
                      placeholder="https://…"
                    />
                    <div className="row g-2">
                      <div className="col-6">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={vendorForm.payment_terms}
                          onChange={(e) => handleVendorFieldChange('payment_terms', e.target.value)}
                          placeholder="Payment Terms (Net 30)"
                        />
                      </div>
                      <div className="col-6">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={vendorForm.minimum_order}
                          onChange={(e) => handleVendorFieldChange('minimum_order', e.target.value)}
                          placeholder="Min. Order"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h6 className="small text-uppercase text-muted mb-2">Ordering</h6>
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {DAYS.map((day) => {
                      const isActive = selectedDays.includes(day.name);
                      return (
                        <button
                          key={day.name}
                          type="button"
                          className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => handleToggleDay(day.name)}
                        >
                          {day.short}
                        </button>
                      );
                    })}
                  </div>
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => applyDayPreset('weekdays')}>Weekdays</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => applyDayPreset('mwf')}>Mon/Wed/Fri</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => applyDayPreset('tth')}>Tue/Thu</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => applyDayPreset('weekend')}>Weekend</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => applyDayPreset('clear')}>Clear</button>
                  </div>
                  <div className="row g-2">
                    <div className="col-md-3">
                      <label className="form-label small mb-1">Order Cutoff</label>
                      <input
                        type="time"
                        className="form-control form-control-sm"
                        value={vendorForm.cutoff_time}
                        onChange={(e) => handleVendorFieldChange('cutoff_time', e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small mb-1">Method</label>
                      <select
                        className="form-select form-select-sm"
                        value={vendorForm.ordering_method}
                        onChange={(e) => handleVendorFieldChange('ordering_method', e.target.value)}
                      >
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="portal">Online Portal</option>
                        <option value="sms">SMS</option>
                        <option value="fax">Fax</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small mb-1">Lead Days</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control form-control-sm"
                        value={vendorForm.lead_days}
                        onChange={(e) => handleVendorFieldChange('lead_days', e.target.value)}
                        placeholder="2"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small mb-1">Status</label>
                      <select
                        className="form-select form-select-sm"
                        value={vendorForm.status}
                        onChange={(e) => handleVendorFieldChange('status', e.target.value)}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="row g-2 mt-0">
                    <div className="col-md-6">
                      <label className="form-label small mb-1">Account #</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={vendorForm.account_number}
                        onChange={(e) => handleVendorFieldChange('account_number', e.target.value)}
                        placeholder="Account Number"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small mb-1">Delivery Days</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={vendorForm.delivery_days}
                        onChange={(e) => handleVendorFieldChange('delivery_days', e.target.value)}
                        placeholder="e.g. Mon, Wed, Fri"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="form-label small mb-1">Notes</label>
                  <textarea
                    rows={2}
                    className="form-control form-control-sm"
                    value={vendorForm.notes}
                    onChange={(e) => handleVendorFieldChange('notes', e.target.value)}
                    placeholder="Auto-discovered from inventory data"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GroupVendorOrderEditor;
